import { ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { Lang } from "@/i18n";
import { fr } from "@/i18n/fr";
import { en } from "@/i18n/en";
import { nl } from "@/i18n/nl";
import { Reveal } from "../Reveal";
import { supabase } from "@/integrations/supabase/client";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";
import { Marquee } from "../Marquee";

interface ToolItem {
  name: string;
  tagline: string;
  description: string;
  status: "live" | "wip";
  url: string;
}

const dicts = { fr, en, nl } as const;

interface DbTool {
  id: string;
  name: string;
  tagline: Record<string, string> | null;
  description: Record<string, string> | null;
  url: string;
  tool_status: "live" | "wip";
  display_order: number;
}

async function fetchPublicTools(): Promise<DbTool[]> {
  const { data, error } = await supabase
    .from("tools")
    .select("id, name, tagline, description, url, tool_status, display_order")
    .eq("status", "published")
    .order("display_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as DbTool[];
}

export function ToolsPage({ lang }: { lang: Lang }) {
  const tools = dicts[lang].tools;
  const liveLabel = tools.statusLive;
  const wipLabel = tools.statusWip;
  const visitLabel = tools.visit;
  const wipNote = tools.wipNote;
  const eyebrow = tools.eyebrow;
  const h1 = tools.h1;
  const sub = tools.sub;

  const { data: dbTools } = useQuery({
    queryKey: ["public", "tools"],
    queryFn: fetchPublicTools,
  });

  const items: ToolItem[] =
    dbTools && dbTools.length > 0
      ? dbTools.map((t) => ({
          name: t.name,
          tagline: t.tagline?.[lang] ?? "",
          description: t.description?.[lang] ?? "",
          status: t.tool_status,
          url: t.url,
        }))
      : (tools.items as ToolItem[]);

  return (
    <>
      <Breadcrumbs lang={lang} current="tools" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
              / {eyebrow}
            </span>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="lg:col-span-8">
              <h1 className="font-display font-black uppercase leading-[1.05] tracking-[-0.045em] text-foreground text-[clamp(2.5rem,8vw,7rem)]">
                {h1}
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Brief
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">{sub}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee
        variant="ink"
        items={["Open source", "Hotel ops", "PMS toolkit"]}
      />

      {/* TOOLS — bento mosaic */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Tools — {String(items.length).padStart(2, "0")}
            </span>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3 md:gap-5">
            {items.map((it, i) => {
              const isLive = it.status === "live";
              const isFeatured = i === 0;
              const tileClass = `bento-tile ${isFeatured ? "is-ink" : i === 2 ? "is-spark" : ""} ${
                isLive ? "tile-fx is-hover" : ""
              } group flex h-full flex-col ${isFeatured ? "p-8 md:p-12" : "p-7 md:p-8"}`;
              const inner = (
                <>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                      {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                    </span>
                    <span
                      className={`border-[1.5px] border-current px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${
                        isLive ? "" : "opacity-60"
                      }`}
                    >
                      {isLive ? liveLabel : wipLabel}
                    </span>
                  </div>
                  <h3
                    className={`font-display mt-8 font-black uppercase leading-[1] tracking-[-0.03em] ${
                      isFeatured ? "text-3xl md:text-6xl" : "text-2xl md:text-3xl"
                    }`}
                  >
                    {it.name}
                  </h3>
                  {it.tagline && (
                    <p className="mt-4 text-[15px] font-medium opacity-90">{it.tagline}</p>
                  )}
                  <p
                    className={`mt-3 leading-relaxed opacity-75 ${
                      isFeatured ? "text-base md:text-lg" : "text-[14px]"
                    }`}
                  >
                    {it.description}
                  </p>
                  <div className="mt-auto pt-8">
                    {isLive ? (
                      <span className="font-display inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] transition-all group-hover:gap-3">
                        {visitLabel} <ArrowUpRight size={16} />
                      </span>
                    ) : (
                      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] opacity-50">
                        {wipNote}
                      </span>
                    )}
                  </div>
                </>
              );
              return (
                <Reveal
                  key={it.name}
                  delay={i * 0.06}
                  className={isFeatured ? "md:col-span-2 md:row-span-2" : ""}
                >
                  {isLive ? (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={tileClass}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div className={tileClass}>{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <RelatedLinks lang={lang} items={["services", "references", "about"]} />
    </>
  );
}
